import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild, inject } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { TranslateService } from '@ngx-translate/core';
import { merge, Observable, of, Subject } from 'rxjs';
import { debounceTime, first, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { Messages } from 'src/app/common/enums/messages';
import { Permission } from 'src/app/common/enums/permissions';
import { AuthService } from 'src/app/core/authentication/auth.service';
import { WorkerService } from 'src/app/data/worker.service';
import { EmploymentHistoryRecordDto } from 'src/app/models/dtos/employment-history-record-dto';
import { EmploymentType } from 'src/app/models/enums/employment-type-enum';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { EmploymentHistoryRecordModalComponent } from '../employment-history-record-modal/employment-history-record-modal.component';
import { EmploymentHistoryDataSource } from '../employment-history.datasource';
import { FormControl } from '@angular/forms';
import { ScreenService } from 'src/app/core/screen.service';
import { EmploymentHistoryRecordService } from 'src/app/data/employment-history-record.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { HttpErrorResponse } from '@angular/common/http';
import { DictionaryItem } from 'src/app/models/dictionary-item';
import { DictionaryService } from 'src/app/data/dictionary.service';

@Component({
  selector: 'app-employment-history-list',
  templateUrl: './employment-history-list.component.html',
  styleUrls: ['./employment-history-list.component.scss'],
  standalone: false
})
export class EmploymentHistoryListComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  displayedColumns: string[] = [];

  dataSource = inject(EmploymentHistoryDataSource);
  private dialog = inject(MatDialog);
  private authService = inject(AuthService);
  private snackbarService = inject(SnackBarService);
  private workerService = inject(WorkerService);
  private translateService = inject(TranslateService);
  private spinner = inject(NgxSpinnerService);
  private dictionaryService = inject(DictionaryService);
  private employmentHistoryRecordService = inject(EmploymentHistoryRecordService);
  public screenService = inject(ScreenService);

  @Input() isLoggedUserProfile: boolean;
  @Input() workerId: number;
  @Input() workerFormContext: boolean;
  @Output() employmentHistoryChange = new EventEmitter<any>();
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  hasNoHistory: boolean = true;
  editingElement: EmploymentHistoryRecordDto | null = null;
  temporaryWorkEmployerControl: FormControl | null = null;
  temporaryWorkEmployers$: Observable<DictionaryItem[]>;
  eligibleEmploymentTypesToAssignEmployerId = new Set([EmploymentType.TemporaryEmploymentAgreement, EmploymentType.TemporaryMandateAgreement]);
  skipEmploymentHistoryInformationChecked = false;
  workerFormApproved = false;
  anyEmploymentHistoryEntry = false;
  searchPerformed = false;

  private readonly timeBetweenInput = 300;
  private readonly minimumInputLetters = 2;
  private readonly unsubscribe$ = new Subject<void>();

  constructor() { }

  ngOnInit(): void {
    this.setDisplayedColumns();
    this.setSkipEmploymentHistory();
  }

  ngAfterViewInit() {
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
    this.translateService.onLangChange.subscribe(() => (this.paginator.pageIndex = 0));

    merge(this.sort.sortChange, this.paginator.page, this.translateService.onLangChange)
      .pipe(tap(() => this.fetchEmploymentHistory()))
      .subscribe();

    this.fetchEmploymentHistory();
  }

  ngOnChanges(changes) {
    if (changes.workerId.firstChange && !!changes.workerId.currentValue) return;
    this.fetchEmploymentHistory();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  isTemporaryWorkEmployerVisible() {
    return (this.hasManageMyExternalWorkersListPermission() || this.hasManageAllExternalWorkersListPermission()) && !this.isLoggedUserProfile;
  }

  startEdit(element: EmploymentHistoryRecordDto): void {
    this.editingElement = element;
    const initialValue = { Id: element.TemporaryWorkEmployerId, Name: element.TemporaryWorkEmployer };
    this.temporaryWorkEmployerControl = new FormControl(initialValue);

    this.temporaryWorkEmployers$ = this.temporaryWorkEmployerControl.valueChanges.pipe(
      startWith(''),
      takeUntil(this.unsubscribe$),
      debounceTime(this.timeBetweenInput),
      switchMap((value: string) => {
        if (value && value.length > this.minimumInputLetters) {
          this.searchPerformed = true;
          return this.dictionaryService.getEmployers(value);
        }

        this.searchPerformed = !!value;
        return of([]);
      }),
    );

    this.temporaryWorkEmployers$ = merge(of([]), this.temporaryWorkEmployers$);
  }

  isPossibleToEdit(element: EmploymentHistoryRecordDto) {
    return this.eligibleEmploymentTypesToAssignEmployerId.has(element.EmploymentTypeId) && !element.WorkerAgreementId;
  }

  finishEditing(): void {
    this.editingElement = null;
    this.temporaryWorkEmployerControl = null;
  }

  isEditing(element: EmploymentHistoryRecordDto): boolean {
    return this.editingElement === element;
  }

  displayValue(value: DictionaryItem): string | undefined {
    return value?.Name;
  }

  saveTemporaryWorkEmployer(element: EmploymentHistoryRecordDto, selectedEmployerId: number | null) {
    this.spinner.show();

    this.employmentHistoryRecordService.setTemporaryWorkEmployer(element.Id, selectedEmployerId).subscribe({
      next: () => {
        this.finishEditing();
        this.snackbarService.openSuccessSnackBar(Messages.SuccessfullySetTemporaryWorkEmployer);
        this.fetchEmploymentHistory();
      },
      complete: () => this.spinner.hide(),
      error: (error: HttpErrorResponse) => {
        this.snackbarService.openErrorSnackBar(error.error);
        this.spinner.hide();
      },
    });
  }

  fetchEmploymentHistory() {
    if (!this.workerId) {
      return;
    }

    this.dataSource.employmentHistorySubject.next({
      WorkerId: this.workerId,
      Page: this.paginator.pageIndex + 1,
      PageSize: this.paginator.pageSize,
      SortingField: this.sort.active,
      SortingDirection: this.sort.direction,
    });
  }

  isCreatedBySystem(record: EmploymentHistoryRecordDto) {
    return record.WorkerAgreementId;
  }

  openModal(record?: EmploymentHistoryRecordDto) {
    this.dialog
      .open(EmploymentHistoryRecordModalComponent, {
        data: { workerId: this.workerId, employmentHistoryRecordId: record?.Id },
      })
      .afterClosed()
      .pipe(first())
      .subscribe((isCreated: boolean) => {
        if (isCreated) {
          this.fetchEmploymentHistory();
          this.setSkipEmploymentHistory();
          this.employmentHistoryChange.emit({
            chxValue: this.skipEmploymentHistoryInformationChecked,
            anyEmploymentHistoryEntry: true,
          });
        }
      });
  }

  onSkipEmploymentHistoryInformationChanged(event: MatCheckboxChange) {
    this.skipEmploymentHistoryInformationChecked = event.checked;
    this.employmentHistoryChange.emit({
      chxValue: this.skipEmploymentHistoryInformationChecked,
      anyEmploymentHistoryEntry: this.anyEmploymentHistoryEntry,
    });
  }

  private setSkipEmploymentHistory(): void {
    this.workerService
      .getEmploymentHistorySkipInformation(this.workerId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res) => {
        this.skipEmploymentHistoryInformationChecked = res.SkipEmploymentHistory;
        this.workerFormApproved = res.WorkerFormApproved;
        this.anyEmploymentHistoryEntry = res.AnyEmploymentHistoryEntry;
      });
  }

  private hasManageAllExternalWorkersListPermission(): boolean {
    return this.authService.hasPermission(Permission.ManageAllExternalWorkersList);
  }

  private hasManageMyExternalWorkersListPermission(): boolean {
    return this.authService.hasPermission(Permission.ManageMyExternalWorkersList);
  }

  private setDisplayedColumns() {
    this.displayedColumns = [
      'employer',
      ...(this.isTemporaryWorkEmployerVisible() ? ['temporaryWorkEmployer'] : []),
      'employmentType',
      'address',
      'employmentDateFrom',
      'employmentDateTo',
      'workingTime',
      'agreementTerminationMethod',
      'actions',
    ];
  }
}
